import React, { useContext } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Button, Grid, LinearProgress, Typography } from '@material-ui/core';
import BackButton from '../../../../../utils/backButton';
import { HelpComponent } from '../../../../../utils/helpComponent';
import { commonStyles } from '../../../../../../styles/common';
import HELP_URL from '../../../../../utils/helpLinkConstant';
import ProjectsTable from '../components/ProjectsTable';
import { useParams } from 'react-router';
import { Content } from '@backstage/core-components';
import { useAddProject } from '../hooks/useAddProject';
import { useProvisionedServiceDetails } from '../hooks/useProvisionedServiceDetails';
import { useGetProvisionedTools } from '../hooks/useGetProvisionedTools';
import { useSettingsOperations } from '../hooks/useSettingsOperations';
import CONSTANTS from '../../../constants';
import { ITPContext, TPContext } from '../../../../providers/TPProvider';
import ReposTable from '../../CodeQAgent/components/ReposTable';
import Refresh from '@material-ui/icons/Refresh';
import { performanceTestStyles } from '../../../../styles/performaceTestStyles';
import DevOpsTable from '../../DevOpsAgent/components/DevOpsTable';

const TIMEOUT_SECONDS = 1000;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    ...commonStyles(theme),
    ...performanceTestStyles(theme),
    '@global': {
      'header + div': {
        display: 'none',
      },
      'header ~ article': {
        padding: '0px !important',
      },
    },
    listPageHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: `${theme.spacing(1.25)}px`,
    },
  }),
);

interface ProjectListPageProps {
  isToolAdmin: boolean;
  service: string;
}

const ProjectListPage = ({ isToolAdmin, service }: ProjectListPageProps) => {
  const classes = useStyles();
  const { projectId: apmNumber } = useParams();
  const { addProject } = useAddProject();
  const { apmInfo }: ITPContext = useContext(TPContext);
  const { updatingFormat, updateAcceptanceCriteriaFormat } =
    useSettingsOperations(apmNumber, true);

  const [provisionedDetails, loading, refreshProvisioned] =
    useProvisionedServiceDetails(apmNumber, service);

  const { ENABLE_JIRAPLANNERBOT, ENABLE_ADOPLANNERBOT, ENABLE_SONARQUBEAGENT } =
    CONSTANTS.TOOLS;

  const [provisionedToolsAPM, isLoading, refreshProvisionedTools] =
    useGetProvisionedTools(apmNumber, service);

  const handleFormatChange = (
    projectId: string,
    format?: string,
    tcFormat?: string,
  ) => {
    const settings = {
      apmnumber: apmNumber,
      projectid: projectId,
      tool: service === 'jira' ? ENABLE_JIRAPLANNERBOT : ENABLE_ADOPLANNERBOT,
      settings: {
        acceptanceCriteriaFormat: format,
        testcaseCustomSetting: tcFormat,
        language: 'en',
      },
    };
    updateAcceptanceCriteriaFormat(settings).then(() => {
      setTimeout(() => {
        refreshProvisioned();
      }, TIMEOUT_SECONDS);
    });
  };

  const handleChange = (project: any) => {
    const projectInstanceId =
      service === 'devops'
        ? project?.projectid
        : project?.instanceid
        ? project?.instanceid
        : service === 'ado'
        ? `${project?.projectorg}-${project?.projectid}`
        : project?.projectid;

    addProject(
      projectInstanceId,
      project?.statusmsg ? project?.statusmsg !== 'Enabled' : true,
      service,
      project?.SonarQubeProjectKey,
      project?.projectorg,
      service === 'devops' ? project?.projectname : undefined,
    ).then(() => {
      setTimeout(() => {
        refreshProvisionedTools();
      }, TIMEOUT_SECONDS);
    });
  };

  return (
    <Content>
      <Grid container spacing={2} className={classes.padding2}>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          className={classes.flexSpacebetween}
        >
          <BackButton />
          <HelpComponent
            helpUrl={
              service === 'sonarqube'
                ? HELP_URL.SONARQUBE_REPOS_TABLE
                : service === 'devops'
                ? HELP_URL.DEVOPS_PROJECTS_TABLE
                : HELP_URL.PERFORMANCE_TEST_RUNS_PAGE
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          className={`${classes.alignItemsCenter} ${classes.flexSpacebetween} ${classes.listPageHeader}`}
        >
          <div
            className={`${classes.flex} ${classes.alignItemsCenter} ${classes.gap1}`}
          >
            <div
              dangerouslySetInnerHTML={{
                __html:
                  (provisionedToolsAPM?.data?.provisioned?.length &&
                    provisionedToolsAPM?.data?.provisioned[0]?.metadata
                      ?.image) ||
                  '',
              }}
            />
            <Typography variant="h5">
              {service === 'sonarqube'
                ? 'CodeQ Agent'
                : service === 'devops'
                ? 'DevOps Agent'
                : service === 'iqe'
                ? 'IQE Agent'
                : `Planner QE Agent`}
            </Typography>
          </div>
          <Button
            variant="contained"
            size="medium"
            onClick={() => refreshProvisionedTools()}
            disabled={!apmInfo?.flows}
            className={`${classes.refreshButton} ${classes.padding1} ${classes.minWidthAuto} ${classes.userCase} ${classes.flex} ${classes.fontBold}`}
            classes={{ label: classes.gapHalf }}
          >
            <Refresh />
            Refresh
          </Button>
        </Grid>
        {(loading || isLoading || updatingFormat) && (
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <LinearProgress />
          </Grid>
        )}
        {!loading &&
          !isLoading &&
          !updatingFormat &&
          (provisionedDetails?.data?.length > 0 ? (
            <Grid item xs={12} sm={12} md={12} lg={12}>
              {service === 'sonarqube' ? (
                <ReposTable
                  isToolAdmin={isToolAdmin}
                  projects={provisionedToolsAPM?.data?.provisioned}
                  repos={apmInfo?.flows}
                  onChange={handleChange}
                />
              ) : service === 'devops' ? (
                <DevOpsTable
                  isToolAdmin={isToolAdmin}
                  projects={provisionedToolsAPM?.data?.provisioned}
                  details={provisionedDetails}
                  onChange={handleChange}
                />
              ) : (
                <ProjectsTable
                  isToolAdmin={isToolAdmin}
                  projects={provisionedToolsAPM?.data?.provisioned ?? []}
                  details={provisionedDetails}
                  onChange={handleChange}
                  onFormatChange={handleFormatChange}
                />
              )}
            </Grid>
          ) : (
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              className={classes.marginLeftSmall}
            >
              <Typography>{`No ${
                service === 'sonarqube' ? 'repos' : 'mapped projects'
              } available.`}</Typography>
            </Grid>
          ))}
      </Grid>
    </Content>
  );
};

export default ProjectListPage;
